import React, { useState, useEffect, useRef } from 'react';
import TextField from '@material-ui/core/TextField';
import numeral from 'numeral';
import { evaluate } from 'mathjs';
import useNotif from 'src/hooks/useNotif';
import { setSelfValueVariable } from '../utils';
import parseShortcutNumber from 'src/helpers/parseShorcutNumber';
import { set } from 'lodash';

const solveExpression = ({ expression = '', variables = {} }) => {
  const exp = [...expression];

  if (exp[0] !== '=') {
    return 'Invalid formula';
  }

  exp.splice(0, 1); // remove '='

  try {
    return evaluate(exp.join(''), variables);
  } catch (error) {
    return 'Invalid formula';
  }
};

const getNumberOfEqualSign = formula =>
  [...formula].filter(char => char === '=').length;

const hasExcludedVariable = (excludedVariables, formula) => {
  if (excludedVariables.length === 0) return false;

  let matches = false;

  excludedVariables.forEach(excludedVariable => {
    if (formula.indexOf(excludedVariable) > -1) {
      matches = true;
    }
  });

  return matches;
};

export const NumberInput = ({
  exp = '',
  value = '',
  onBlur = () => null,
  onChange = () => null,
  onError = () => null,
  variables = {},
  excludedVariables = [],
  disableDecimal = false,
  format = '0,0.00',
  balanceShortcut = false,
  enableCtrlCmdArrowCaretMove = true,
  ...rest
}) => {
  const ref = useRef(null);
  const formulaRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  // formula
  const [isFormulaMode, setIsFormulaMode] = useState(false);
  const [formula, setFormula] = useState('');
  const [submitted, setSubmitted] = useState(true);

  // value number
  const [numberValue, setNumberValue] = useState(0);
  const { error: errorNotif } = useNotif();

  const { require, isEditable, placeholder, error, errorMessage } = rest;
  const externalInputRef = rest?.textboxProps?.inputRef;
  const numericInputRef = ref; // existing numeric input ref
  const proxyRef = useRef({
    focus: () => {
      // ensure component enters focus mode then focus the numeric input
      setFocus();
      setTimeout(() => {
        if (numericInputRef.current) numericInputRef.current.focus();
      }, 0);
    }
  });

  useEffect(() => {
    if (!externalInputRef) return;
    if (typeof externalInputRef === 'function') {
      externalInputRef(proxyRef.current);
    } else if (typeof externalInputRef === 'object') {
      externalInputRef.current = proxyRef.current;
    }
  }, [externalInputRef]);

  // remove inputRef from textboxProps before spreading into internal TextFields to avoid overriding
  const {
    inputRef: _ignoredInputRef,
    onKeyDown: extOnKeyDown,
    ...safeTextboxProps
  } = rest?.textboxProps || {};

  const setFocus = () => {
    setIsFocused(true);
    setNumberValue(value);
    setSubmitted(false);

    if (exp !== '') {
      setFormula(exp);
      setIsFormulaMode(true);
    }
  };

  const handleShiftArrowCaretMove = e => {
    if (!enableCtrlCmdArrowCaretMove) return false;
    if (e?.shiftKey) return false;
    if (!(e?.ctrlKey || e?.metaKey)) return false;
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return false;

    const el = e.target;
    if (!el || typeof el.selectionStart !== 'number') return false;
    if (typeof el.setSelectionRange !== 'function') return false;

    e.preventDefault();
    e.stopPropagation();

    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const len = (el.value ?? '').toString().length;

    // Collapse selection first, then move caret by 1.
    let pos = e.key === 'ArrowLeft' ? Math.min(start, end) : Math.max(start, end);
    pos = e.key === 'ArrowLeft' ? Math.max(0, pos - 1) : Math.min(len, pos + 1);

    try {
      el.setSelectionRange(pos, pos);
    } catch {}

    return true;
  };

  const handleKeyDownDisplay = e => {
    if (!balanceShortcut) return false;
    const key = e.key;
    const isOp = key === '+' || key === '-' || key === '*' || key === '/';
    // Use prop value for emptiness when in display mode
    const isEmpty =
      value === '' ||
      value === null ||
      typeof value === 'undefined' ||
      Number(value) === 0;
    if (!isFormulaMode && isOp && isEmpty) {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      setIsFocused(true);
      setSubmitted(false);
      setIsFormulaMode(true);
      const prefill = `=bal${key}`;
      setFormula(prefill);
      setTimeout(() => {
        if (formulaRef.current) {
          formulaRef.current.focus();
          const len = prefill.length;
          try {
            formulaRef.current.setSelectionRange(len, len);
          } catch {}
        }
      }, 0);
      return true;
    }
    return false;
  };

  const submitExpression = e => {
    setIsFocused(false);

    if (formula && formula !== '') {
      setIsFormulaMode(false);

      const expression = setSelfValueVariable({ value, formula });

      let result = solveExpression({ expression, variables });

      if (result === 'Invalid formula') {
        onChange({
          value: 0,
          formula
        });

        onError('Invalid formula ' + formula);

        return;
      }

      const finalValue = result === 0 ? +numberValue : result;

      if (hasExcludedVariable(excludedVariables, formula)) {
        onChange({
          value: finalValue,
          formula: ''
        });
      } else {
        onChange({
          value: finalValue,
          formula
        });
      }

      return;
    }

    onChange({ value: +numberValue, formula: '' });
    setIsFormulaMode(false);
  };

  const handleBlurNumericInput = () => {
    if (isFormulaMode) return;

    if (submitted) return;

    setSubmitted(true);
    submitExpression();
  };

  const handleBlurFormula = () => {
    if (isFocused && formula === '') return;

    if (submitted) return;

    setSubmitted(true);
    submitExpression();
  };

  const handleKeyPress = e => {
    let key = e.key || e.which;

    if (key === 'Enter') {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      setSubmitted(true);
      submitExpression();
    }
  };

  const handleKeyDownNumeric = e => {
    if (!balanceShortcut) return false;
    const key = e.key;
    const isOp = key === '+' || key === '-' || key === '*' || key === '/';
    const isEmpty =
      numberValue === '' ||
      numberValue === null ||
      typeof numberValue === 'undefined' ||
      Number(numberValue) === 0;
    if (!isFormulaMode && isEmpty && isOp) {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      setIsFormulaMode(true);
      const prefill = `=bal${key}`;
      setFormula(prefill);
      setSubmitted(false);
      // Move focus to formula input after render
      setTimeout(() => {
        if (formulaRef.current) {
          formulaRef.current.focus();
          const len = prefill.length;
          try {
            formulaRef.current.setSelectionRange(len, len);
          } catch {}
        }
      }, 0);
      return true;
    }
    return false;
  };

  const handleKeyPressFormula = e => {
    let key = e.key || e.which;

    if (key === 'Enter') {
      if (e && typeof e.preventDefault === 'function') e.preventDefault();
      if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
      setSubmitted(true);
      submitExpression();
    }
  };

  const handleChange = e => {
    const { value } = e.target;

    if (value === '') {
      setNumberValue(value);
      onChange({
        value: '',
        formula: ''
      });
      return;
    }

    if (value === '=') {
      setIsFormulaMode(true);
      setFormula('=');
      return;
    }

    const firstItem = value?.[0] ?? '';
    if (firstItem === '=') {
      setIsFormulaMode(true);
      setFormula(value);
      return;
    }

    if (value === '-') {
      setNumberValue(value);
      return;
    }

    if (value === '.') {
      setNumberValue('0.');
      return;
    }

    const shortcutNum = parseShortcutNumber(value);

    if (shortcutNum !== 0) {
      if (disableDecimal && shortcutNum % 1 !== 0) {
        errorNotif('Decimal not allowed.');
        return;
      }

      setNumberValue(shortcutNum);
      onChange({
        value: +shortcutNum,
        formula: ''
      });

      return;
    }

    const parsedValue = value.toString().replace(/[^a-zA-Z0-9.-]/g, '', '');

    if (isNaN(parsedValue)) return;

    if (disableDecimal && +parsedValue % 1 !== 0) {
      errorNotif('Decimal not allowed.');
      return;
    }

    setNumberValue(parsedValue);
  };

  const handleChangeFormula = e => {
    const { value } = e.target;

    if (value === '') {
      setIsFormulaMode(false);
      setFormula('');
      setNumberValue('');
      onChange({
        value: '',
        formula: ''
      });

      return;
    }

    if (getNumberOfEqualSign(value) > 1) return;

    if (value?.[0] !== '=') return;

    setFormula(value);
  };

  useEffect(() => {
    setNumberValue(value);
    setFormula(exp);
  }, [value, exp]);

  useEffect(() => {
    if (isFocused && !isFormulaMode && ref.current) {
      const len = ref.current.value.toString().length;
      ref.current.focus();
      // Place caret at end (avoid auto-selecting whole value)
      try {
        ref.current.setSelectionRange(len, len);
      } catch {}
    }
  }, [ref.current, isFocused, isFormulaMode]);

  useEffect(() => {
    if (isFocused && isFormulaMode && formulaRef.current)
      formulaRef.current.focus();
  }, [formulaRef.current, isFocused, isFormulaMode]);

  return (
    <>
      <TextField
        variant="outlined"
        value={
          value === '' || value === null || typeof value === 'undefined'
            ? ''
            : numeral(value).format(disableDecimal ? '' : format)
        }
        required={require}
        disabled={isEditable}
        onFocus={setFocus}
        onKeyDown={e => {
          const handledShift = handleShiftArrowCaretMove(e);
          if (handledShift) return;
          const handled = handleKeyDownDisplay(e);
          if (!handled && typeof extOnKeyDown === 'function') extOnKeyDown(e);
        }}
        autoComplete="off"
        inputProps={{
          style: { textAlign: 'right' }
        }}
        style={{ display: !isFocused && !isFormulaMode ? 'block' : 'none' }}
        error={error}
        helperText={errorMessage}
        placeholder={placeholder}
        label={rest.label}
        fullWidth
        {...safeTextboxProps}
      />
      <TextField
        variant="outlined"
        value={numberValue}
        required={require}
        disabled={isEditable}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        onKeyDown={e => {
          const handledShift = handleShiftArrowCaretMove(e);
          if (handledShift) return;
          const handled = handleKeyDownNumeric(e);
          if (!handled && typeof extOnKeyDown === 'function') extOnKeyDown(e);
        }}
        onFocus={setFocus}
        onBlur={handleBlurNumericInput}
        autoComplete="off"
        inputRef={ref}
        inputProps={{
          style: { textAlign: 'right' }
        }}
        error={error}
        helperText={errorMessage}
        placeholder={placeholder}
        label={rest.label}
        fullWidth
        style={{ display: isFocused && !isFormulaMode ? 'block' : 'none' }}
        {...safeTextboxProps}
      />
      <TextField
        variant="outlined"
        value={formula}
        required={require}
        disabled={isEditable}
        error={error}
        helperText={errorMessage}
        onChange={handleChangeFormula}
        onKeyPress={handleKeyPressFormula}
        onKeyDown={e => {
          const handledShift = handleShiftArrowCaretMove(e);
          if (handledShift) return;
          if (typeof extOnKeyDown === 'function') extOnKeyDown(e);
        }}
        onBlur={handleBlurFormula}
        label={rest.label}
        fullWidth
        inputRef={formulaRef}
        style={{ display: isFocused && isFormulaMode ? 'block' : 'none' }}
        {...safeTextboxProps}
      />
    </>
  );
};
